import { useCallback, useRef } from "react";
import { useUserLog } from "@/shared/lib/userLog";
import { CampaignStatusTrackingData } from "@/api/campaignStatusTracking";
import { buildStageDistribution } from "./campaignStatusTrackingUtils";

export type StatusTrackingViewContext = "Agency View" | "Client View";

interface StatusTrackingContextProps {
	campaignId: string;
	viewContext: StatusTrackingViewContext;
	userRole: string;
	totalRoster: number;
}

export function useCampaignStatusTrackingAnalytics(context: StatusTrackingContextProps) {
	const { logEvent } = useUserLog();
	const viewedAtRef = useRef<number | null>(null);
	const firstInteractionRef = useRef(false);
	const interactedRef = useRef(false);

	const baseProperties = useCallback(
		(data?: CampaignStatusTrackingData) => ({
			view_context: context.viewContext,
			total_roster: context.totalRoster,
			user_role: context.userRole,
			session_id: null,
			...(data
				? {
						stage_distribution: buildStageDistribution(data),
						last_updated_at: data.lastUpdatedAt,
						is_empty_state: data.totalRoster === 0,
						has_dropped: data.droppedCount > 0,
					}
				: {}),
		}),
		[context.totalRoster, context.userRole, context.viewContext],
	);

	const trackOverviewViewed = useCallback(
		(data: CampaignStatusTrackingData) => {
			viewedAtRef.current = Date.now();
			firstInteractionRef.current = false;
			interactedRef.current = false;
			logEvent({
				action: "status_overview_viewed",
				campaign_id: context.campaignId,
				properties: baseProperties(data),
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackStageCardClicked = useCallback(
		(stage: string, countAtClick: number, cardRank: number, data: CampaignStatusTrackingData) => {
			interactedRef.current = true;
			if (!firstInteractionRef.current) {
				firstInteractionRef.current = true;
				const viewedAt = viewedAtRef.current;
				logEvent({
					action: "time_to_first_interaction",
					campaign_id: context.campaignId,
					duration: viewedAt != null ? Date.now() - viewedAt : null,
					properties: {
						...baseProperties(data),
						ms_since_view: viewedAt != null ? Date.now() - viewedAt : null,
						first_target: "card",
					},
				});
			}

			logEvent({
				action: "stage_card_clicked",
				campaign_id: context.campaignId,
				properties: {
					...baseProperties(data),
					stage,
					count_at_click: countAtClick,
					card_rank: cardRank,
				},
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackCreatorHandleClicked = useCallback(
		(stage: string, creatorId: string, destination: string, data: CampaignStatusTrackingData) => {
			interactedRef.current = true;
			if (!firstInteractionRef.current) {
				firstInteractionRef.current = true;
				const viewedAt = viewedAtRef.current;
				logEvent({
					action: "time_to_first_interaction",
					campaign_id: context.campaignId,
					duration: viewedAt != null ? Date.now() - viewedAt : null,
					properties: {
						...baseProperties(data),
						ms_since_view: viewedAt != null ? Date.now() - viewedAt : null,
						first_target: "handle",
					},
				});
			}

			logEvent({
				action: "creator_handle_clicked",
				campaign_id: context.campaignId,
				item_id: creatorId,
				properties: {
					...baseProperties(data),
					stage,
					creator_id: creatorId,
					destination,
				},
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackEmptyStateShown = useCallback(
		(data: CampaignStatusTrackingData) => {
			logEvent({
				action: "empty_state_shown",
				campaign_id: context.campaignId,
				properties: baseProperties(data),
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackInvalidStageDetected = useCallback(
		(creatorId: string, rawStageValue: string | null, data: CampaignStatusTrackingData) => {
			logEvent({
				action: "invalid_stage_detected",
				campaign_id: context.campaignId,
				item_id: creatorId,
				properties: {
					...baseProperties(data),
					creator_id: creatorId,
					raw_stage_value: rawStageValue,
				},
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackReconciliationMismatch = useCallback(
		(data: CampaignStatusTrackingData) => {
			logEvent({
				action: "reconciliation_mismatch",
				campaign_id: context.campaignId,
				properties: {
					...baseProperties(data),
					expected_total: data.reconciliation.expectedTotal,
					summed_total: data.reconciliation.summedTotal,
					delta: data.reconciliation.delta,
				},
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	const trackOverviewDwellTime = useCallback(
		(data: CampaignStatusTrackingData) => {
			const viewedAt = viewedAtRef.current;
			if (viewedAt == null) return;

			logEvent({
				action: "overview_dwell_time",
				campaign_id: context.campaignId,
				duration: Date.now() - viewedAt,
				properties: {
					...baseProperties(data),
					dwell_ms: Date.now() - viewedAt,
					interacted: interactedRef.current,
				},
			});
		},
		[baseProperties, context.campaignId, logEvent],
	);

	return {
		trackOverviewViewed,
		trackStageCardClicked,
		trackCreatorHandleClicked,
		trackEmptyStateShown,
		trackInvalidStageDetected,
		trackReconciliationMismatch,
		trackOverviewDwellTime,
	};
}
